import React, { useMemo } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../../store/user/user'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../common/integrations'
import { type ButtonVariant } from '../../../../components/Button/Button'
import { ActionsDropdownItem, ActionsToggle, CrossClientActionsDropdown, type CrmAction, type CrmActionsMap } from '../../Actions/CrossClientActionsDropdown'
import { CRM_ACTION_REGISTRY, type CrmActionKind } from '../../Actions/crmActionRegistry'
import { useCrmActionHost } from '../../Actions/useCrmActionHost'
import { useActionTracking, type CrossClientActionScope } from '../../Actions/useActionTracking'
import { useZapierAction } from '../../Actions/useZapierAction'
import { ReactComponent as ZapierLogo } from '../../../../assets/zapier_logo.svg'
import { useExportCrossClientResults } from '../hooks'

export interface CrossClientSingleActionTarget {
  name?: string
  clientId: string
  integrationServiceIds?: IntegrationServiceId[]
}

interface CrossClientChooseActionDropdownProps {
  count: number
  scope: CrossClientActionScope
  resolveTargets: () => Promise<CrossClientQueryResult[]>
  label?: string
  toggleVariant?: ButtonVariant
  toggleClassName?: string
  extraItems?: React.ReactNode
  disabled?: boolean
  singleClient?: CrossClientSingleActionTarget
}

export const CrossClientChooseActionDropdown: React.FC<CrossClientChooseActionDropdownProps> = ({
  count,
  scope,
  resolveTargets,
  label = 'Choose action',
  toggleVariant = 'primary',
  toggleClassName = 'button-medium rounded-3 text-nowrap',
  extraItems,
  disabled = false,
  singleClient,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { open } = useCrmActionHost()
  const { trackActionClicked } = useActionTracking(scope)
  const { exportCSV } = useExportCrossClientResults(resolveTargets)
  const { openZapier, zapierModal } = useZapierAction()

  const crmActions = useMemo<CrmActionsMap>(() => {
    const connectedServiceIds = new Set((currentUser?.integrations ?? []).map((integration) => integration.serviceId))

    const map: CrmActionsMap = {}
    for (const [kind, descriptor] of Object.entries(CRM_ACTION_REGISTRY) as [CrmActionKind, (typeof CRM_ACTION_REGISTRY)[CrmActionKind]][]) {
      if (!connectedServiceIds.has(descriptor.serviceId)) {
        continue
      }
      const action: CrmAction = {
        label: descriptor.label,
        icon: descriptor.icon,
        onClick: () => {
          trackActionClicked(getIntegrationDisplayName(descriptor.serviceId), descriptor.label)
          open(kind, { resolveResults: resolveTargets, scope })
        },
      }
      const existing = map[descriptor.serviceId] ?? []
      map[descriptor.serviceId] = [...existing, action]
    }
    return map
  }, [currentUser?.integrations, open, resolveTargets, scope, trackActionClicked])

  return (
    <>
      <CrossClientActionsDropdown
        count={count}
        singleClientName={singleClient?.name}
        clientId={singleClient?.clientId}
        clientIntegrationServiceIds={singleClient?.integrationServiceIds}
        crmActions={crmActions}
        toggle={
          <ActionsToggle variant={toggleVariant} className={toggleClassName} disabled={disabled}>
            {label}
            <i className='bi bi-chevron-expand ms-1' />
          </ActionsToggle>
        }
      >
        {extraItems}
        <ActionsDropdownItem
          icon='bi-file-earmark-spreadsheet'
          label='Export as CSV'
          onClick={() => {
            trackActionClicked('Zocks', 'Export as CSV')
            exportCSV()
          }}
        />
        <ActionsDropdownItem iconNode={<ZapierLogo width={16} height={16} />} label='Send to Zapier' onClick={openZapier} />
      </CrossClientActionsDropdown>
      {zapierModal}
    </>
  )
}
